import { AXIOS_INSTANCE, authCreateSession } from '@cocoonspace/api'

import { getUserToken } from './utils'

/**
 * Response header set by the API on every response, carrying the expiration
 * date of the current session cookie.
 */
export const SESSION_EXPIRES_AT_HEADER = 'x-session-expires-at'

/** Path of the session endpoint (`POST` creates/renews the cookie). */
const SESSION_ENDPOINT_PATH = '/auth/session'

/** Refresh the session cookie when it expires in 3 days or less. */
export const SESSION_REFRESH_THRESHOLD_MS = 3 * 24 * 60 * 60 * 1000

/** Minimum delay between two refresh attempts (avoids retry storms). */
const REFRESH_COOLDOWN_MS = 60_000

/**
 * Parses the `X-Session-Expires-At` header value into a unix timestamp (ms).
 * Accepts ISO 8601 date strings as well as unix timestamps in seconds or
 * milliseconds. Returns `null` when the value cannot be parsed.
 */
export const parseSessionExpiresAt = (value: unknown): number | null => {
	if (typeof value === 'number') {
		return Number.isFinite(value) ? normalizeEpoch(value) : null
	}

	if (typeof value !== 'string') return null

	const trimmed = value.trim()
	if (!trimmed) return null

	if (/^\d+$/.test(trimmed)) return normalizeEpoch(Number(trimmed))

	const parsed = Date.parse(trimmed)
	return Number.isNaN(parsed) ? null : parsed
}

// Epochs below ~Sep 2001 in milliseconds are actually expressed in seconds.
const UNIX_MS_THRESHOLD = 1e12

const normalizeEpoch = (epoch: number): number =>
	Math.abs(epoch) < UNIX_MS_THRESHOLD ? epoch * 1000 : epoch

/** Whether the session cookie expiring at `expiresAtMs` should be refreshed now. */
export const shouldRefreshSession = (
	expiresAtMs: number,
	nowMs: number = Date.now(),
): boolean => expiresAtMs - nowMs <= SESSION_REFRESH_THRESHOLD_MS

/**
 * Exchanges the current Firebase ID token for a session cookie by calling
 * `POST /auth/session`. The cookie is `HttpOnly` and sent automatically with
 * every API request (the axios instance uses `withCredentials`).
 *
 * Never throws: returns `false` when there is no authenticated user or when
 * the request fails, so callers can fire-and-forget it after a login.
 */
export const createSessionCookie = async (): Promise<boolean> => {
	try {
		const token = await getUserToken()

		if (!token) return false

		await authCreateSession({
			headers: { Authorization: `Bearer ${token}` },
		})

		return true
	} catch (error) {
		console.warn('[session-cookie] failed to create the session cookie', error)
		return false
	}
}

let refreshInFlight: Promise<boolean> | null = null
let lastRefreshAttemptAt = 0

/**
 * Refreshes the session cookie through the same `POST /auth/session` endpoint
 * used at login. Concurrent calls share a single in-flight request, and failed
 * attempts are retried at most once per `REFRESH_COOLDOWN_MS`.
 */
export const refreshSessionCookie = async (): Promise<void> => {
	if (refreshInFlight) {
		await refreshInFlight
		return
	}

	if (Date.now() - lastRefreshAttemptAt < REFRESH_COOLDOWN_MS) return

	lastRefreshAttemptAt = Date.now()
	refreshInFlight = createSessionCookie()

	try {
		await refreshInFlight
	} finally {
		refreshInFlight = null
	}
}

// Minimal structural shape of an axios response. Typed structurally because
// the API SDK bundles its own axios version, whose types differ from the
// workspace one.
interface ApiResponseLike {
	headers: unknown
	config: { url?: string | undefined }
}

const readSessionExpiresAt = (headers: unknown): number | null => {
	if (typeof headers !== 'object' || headers === null) return null

	// Axios normalizes response header names to lowercase.
	return parseSessionExpiresAt(
		(headers as Record<string, unknown>)[SESSION_EXPIRES_AT_HEADER],
	)
}

const maybeRefreshSessionCookie = (response: ApiResponseLike): void => {
	// The session endpoint renews the cookie itself: never refresh from its
	// own response, otherwise every refresh would trigger another one.
	if (response.config.url?.includes(SESSION_ENDPOINT_PATH)) return

	const expiresAtMs = readSessionExpiresAt(response.headers)
	if (expiresAtMs === null || !shouldRefreshSession(expiresAtMs)) return

	void refreshSessionCookie().catch(() => {})
}

let isRefreshSetup = false

/**
 * Registers a response interceptor on the API v3 axios instance that watches
 * the `X-Session-Expires-At` header of every response and refreshes the
 * session cookie when it expires in 3 days or less.
 *
 * Idempotent: safe to call from every app initialisation.
 */
export const setupSessionCookieRefresh = (): void => {
	if (isRefreshSetup) return
	isRefreshSetup = true

	AXIOS_INSTANCE.interceptors.response.use((response) => {
		maybeRefreshSessionCookie(response)
		return response
	})
}
